import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import { createEmployeeColorMap } from "@/components/reports/utilization/employee-color-palette";
import { startOfWeek, startOfMonth, format, parseISO } from "date-fns";

export interface UtilizationReportFilters {
  dateFrom: string;
  dateTo: string;
  userId?: string;
  userIds?: string[];
  entryType?: "billable" | "non-billable" | "all";
}

export interface UtilizationByUser {
  userId: string;
  userName: string;
  loggedHours: number;
  billableHours: number;
  nonBillableHours: number;
  expectedHours: number;
  utilizationPct: number;
  availableHoursPerWeek: number;
}

export interface EntriesByUser {
  userId: string;
  userName: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
}

export interface PeriodHours {
  period: string;
  periodLabel: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  byUser: Record<string, number>;
}

export interface RawTimeEntry {
  id: string;
  user_id: string;
  user_name: string;
  date_worked: string;
  hours: number;
  ticket_title: string;
  description: string;
  entry_type: string;
  start_time: string | null;
  end_time: string | null;
}

export interface UtilizationReportData {
  entriesByUser: EntriesByUser[];
  entriesByDay: PeriodHours[];
  entriesByWeek: PeriodHours[];
  entriesByMonth: PeriodHours[];
  utilizationByUser: UtilizationByUser[];
  rawEntries: RawTimeEntry[];
  employeeColorMap: Record<string, string>;
  totalHours: number;
  totalBillableHours: number;
  totalNonBillableHours: number;
  topContributors: EntriesByUser[];
}

function getExpectedHours(
  dateFrom: Date,
  dateTo: Date,
  availableHoursPerWeek: number
): number {
  const daysDiff = Math.ceil((dateTo.getTime() - dateFrom.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const weeks = daysDiff / 7;
  return Math.round(weeks * availableHoursPerWeek * 100) / 100;
}

export function useUtilizationReport(filters: UtilizationReportFilters) {
  return useQuery({
    queryKey: ["utilization-report", filters],
    queryFn: async (): Promise<UtilizationReportData> => {
      const supabase = getSupabase();
      const dateFrom = parseISO(filters.dateFrom);
      const dateTo = parseISO(filters.dateTo);

      let query = supabase
        .from("time_entries")
        .select(
          `
          id,
          user_id,
          hours,
          entry_type,
          date_worked,
          start_time,
          end_time,
          description,
          ticket:tickets(id, title, ticket_number),
          user:app_users!time_entries_user_id_fkey(id, email, first_name, last_name, available_hours)
        `
        )
        .gte("date_worked", filters.dateFrom)
        .lte("date_worked", filters.dateTo)
        .order("date_worked", { ascending: true });

      if (filters.userId) {
        query = query.eq("user_id", filters.userId);
      }
      if (filters.userIds && filters.userIds.length > 0) {
        query = query.in("user_id", filters.userIds);
      }
      if (filters.entryType === "billable") {
        query = query.eq("entry_type", "billable");
      } else if (filters.entryType === "non-billable") {
        query = query.neq("entry_type", "billable");
      }

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching utilization report", error);
        throw new Error(error.message);
      }

      const entries = (data || []) as any[];

      const userMap = new Map<
        string,
        {
          userName: string;
          totalHours: number;
          billableHours: number;
          nonBillableHours: number;
          availableHours: number;
        }
      >();

      const dayMap = new Map<string, { total: number; billable: number; byUser: Map<string, number> }>();
      const weekMap = new Map<string, { total: number; billable: number; byUser: Map<string, number> }>();
      const monthMap = new Map<string, { total: number; billable: number; byUser: Map<string, number> }>();

      const rawEntries: RawTimeEntry[] = [];

      for (const entry of entries) {
        const userId = entry.user_id;
        const userName =
          entry.user?.first_name || entry.user?.last_name
            ? `${entry.user?.first_name || ""} ${entry.user?.last_name || ""}`.trim()
            : entry.user?.email || "Unknown User";
        const hours = Number(entry.hours) || 0;
        const isBillable = entry.entry_type === "billable";
        const availableHours = entry.user?.available_hours ?? 40;

        if (!userMap.has(userId)) {
          userMap.set(userId, {
            userName,
            totalHours: 0,
            billableHours: 0,
            nonBillableHours: 0,
            availableHours,
          });
        }
        const userData = userMap.get(userId)!;
        userData.totalHours += hours;
        if (isBillable) {
          userData.billableHours += hours;
        } else {
          userData.nonBillableHours += hours;
        }

        const dateStr = entry.date_worked;
        const date = parseISO(dateStr);
        const weekStart = format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const monthStart = format(startOfMonth(date), "yyyy-MM");

        if (!dayMap.has(dateStr)) {
          dayMap.set(dateStr, { total: 0, billable: 0, byUser: new Map() });
        }
        const dayData = dayMap.get(dateStr)!;
        dayData.total += hours;
        if (isBillable) dayData.billable += hours;
        dayData.byUser.set(userId, (dayData.byUser.get(userId) || 0) + hours);

        if (!weekMap.has(weekStart)) {
          weekMap.set(weekStart, { total: 0, billable: 0, byUser: new Map() });
        }
        const weekData = weekMap.get(weekStart)!;
        weekData.total += hours;
        if (isBillable) weekData.billable += hours;
        weekData.byUser.set(userId, (weekData.byUser.get(userId) || 0) + hours);

        if (!monthMap.has(monthStart)) {
          monthMap.set(monthStart, { total: 0, billable: 0, byUser: new Map() });
        }
        const monthData = monthMap.get(monthStart)!;
        monthData.total += hours;
        if (isBillable) monthData.billable += hours;
        monthData.byUser.set(userId, (monthData.byUser.get(userId) || 0) + hours);

        rawEntries.push({
          id: entry.id,
          user_id: userId,
          user_name: userName,
          date_worked: dateStr,
          hours,
          ticket_title: entry.ticket?.title || "No Ticket",
          description: entry.description || "",
          entry_type: entry.entry_type || "Standard",
          start_time: entry.start_time,
          end_time: entry.end_time,
        });
      }

      const entriesByUser: EntriesByUser[] = Array.from(userMap.entries())
        .map(([userId, data]) => ({
          userId,
          userName: data.userName,
          totalHours: Math.round(data.totalHours * 100) / 100,
          billableHours: Math.round(data.billableHours * 100) / 100,
          nonBillableHours: Math.round(data.nonBillableHours * 100) / 100,
        }))
        .sort((a, b) => b.totalHours - a.totalHours);

      const utilizationByUser: UtilizationByUser[] = entriesByUser.map((u) => {
        const userData = userMap.get(u.userId)!;
        const expectedHours = getExpectedHours(dateFrom, dateTo, userData.availableHours);
        const utilizationPct =
          expectedHours > 0 ? Math.round((u.totalHours / expectedHours) * 1000) / 10 : 0;
        return {
          userId: u.userId,
          userName: u.userName,
          loggedHours: u.totalHours,
          billableHours: u.billableHours,
          nonBillableHours: u.nonBillableHours,
          expectedHours,
          utilizationPct,
          availableHoursPerWeek: userData.availableHours,
        };
      });

      const toPeriodHours = (
        map: Map<string, { total: number; billable: number; byUser: Map<string, number> }>,
        sortKey: (k: string) => Date
      ): PeriodHours[] => {
        return Array.from(map.entries())
          .sort((a, b) => sortKey(a[0]).getTime() - sortKey(b[0]).getTime())
          .map(([period, data]) => {
            const byUser: Record<string, number> = {};
            data.byUser.forEach((h, uid) => byUser[uid] = h);
            return {
              period,
              periodLabel: period,
              totalHours: Math.round(data.total * 100) / 100,
              billableHours: Math.round(data.billable * 100) / 100,
              nonBillableHours: Math.round((data.total - data.billable) * 100) / 100,
              byUser,
            };
          });
      };

      const entriesByDay = toPeriodHours(dayMap, (k) => parseISO(k));
      const entriesByWeek = toPeriodHours(weekMap, (k) => parseISO(k));
      const entriesByMonth = toPeriodHours(monthMap, (k) => parseISO(k + "-01"));

      const userNames: Record<string, string> = {};
      entriesByUser.forEach((u) => (userNames[u.userId] = u.userName));
      const employeeColorMap = createEmployeeColorMap(
        entriesByUser.map((u) => u.userId),
        userNames
      );

      const totalHours = entriesByUser.reduce((s, u) => s + u.totalHours, 0);
      const totalBillableHours = entriesByUser.reduce((s, u) => s + u.billableHours, 0);
      const totalNonBillableHours = entriesByUser.reduce((s, u) => s + u.nonBillableHours, 0);

      const topContributors = entriesByUser.slice(0, 5);

      return {
        entriesByUser,
        entriesByDay,
        entriesByWeek,
        entriesByMonth,
        utilizationByUser,
        rawEntries,
        employeeColorMap,
        totalHours,
        totalBillableHours,
        totalNonBillableHours,
        topContributors,
      };
    },
    enabled: !!filters.dateFrom && !!filters.dateTo,
    staleTime: 2 * 60 * 1000,
  });
}
